import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography, LinearProgress, Alert, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, Tooltip, Switch, FormControlLabel } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/apiService';
import { useNavigate } from 'react-router-dom';

const SyncInspector: React.FC = () => {
  const { keycloak } = useAuth();
  const navigate = useNavigate();
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [shards, setShards] = useState<any[]>([]);
  const [allowProbe, setAllowProbe] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [shardsLoadError, setShardsLoadError] = useState<string | null>(null);
  const [jobStatuses, setJobStatuses] = useState<Record<string, any>>({});
  const [queueDialog, setQueueDialog] = useState<{ open: boolean; kind: 'bill'|'member'|'maintenance'|''; items: any[]; total: number; includeDLQ: boolean; loading: boolean; error: string | null }>({ open: false, kind: '', items: [], total: 0, includeDLQ: false, loading: false, error: null });
  const [connectivity, setConnectivity] = useState<{ healthOk: boolean | null; infoOk: boolean | null; detail?: string | null }>({ healthOk: null, infoOk: null, detail: null });

  const isAdministrator = useMemo(() => {
    const ADMIN_NAME = (process.env.REACT_APP_ADMIN_GROUP_NAME || 'administrator');
    const tokenParsed: any = keycloak?.tokenParsed || {};
    const groups: string[] = tokenParsed?.groups || tokenParsed?.realm_access?.groups || [] as any;
    // Policy: Only group membership confers admin rights
    const hasGroup = Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === ADMIN_NAME);
    return hasGroup;
  }, [keycloak]);

  const load = async () => {
    if (keycloak?.token) apiService.setToken(keycloak.token);
    setLoading(true);
    setError(null);
    setShardsLoadError(null);
    // Connectivity probes (non-versioned endpoints via /api)
    try {
      const [h, i] = await Promise.allSettled([
        apiService.getHealthStatus(),
        apiService.getAppInfo(),
      ]);
      const healthOk = (h.status === 'fulfilled');
      const infoOk = (i.status === 'fulfilled');
      let detail: string | null = null;
      if (!healthOk || !infoOk) {
        const errObj: any = (h.status === 'rejected' ? (h as any).reason : (i.status === 'rejected' ? (i as any).reason : null));
        const msg = errObj?.message || errObj?.code || 'Network error';
        detail = msg;
      }
      setConnectivity({ healthOk, infoOk, detail });
    } catch (e: any) {
      setConnectivity({ healthOk: false, infoOk: false, detail: e?.message || 'Network error' });
    }
    // Load status first; do not block shards on failure
    const classifyNetErr = (e: any): boolean => {
      // true when no response was received (likely TLS/CORS/network)
      return !e?.response;
    };
    try {
      const st = await apiService.getAdminSyncStatus();
      setSyncStatus(st.data || {});
    } catch (e: any) {
      // one quick retry on network-only failures
      if (classifyNetErr(e)) {
        try {
          await new Promise(res => setTimeout(res, 500));
          const st2 = await apiService.getAdminSyncStatus();
          setSyncStatus(st2.data || {});
        } catch (e2: any) {
          const detail = e2?.response?.data?.detail || e2?.message || 'Failed to load sync status';
          setError(detail);
          setSyncStatus({ queues: {}, status: 'unknown', error: detail });
        }
      } else {
        const detail = e?.response?.data?.detail || e?.message || 'Failed to load sync status';
        setError(detail);
        setSyncStatus({ queues: {}, status: 'unknown', error: detail });
      }
    }
    // Load shards separately so a network/proxy/TLS hiccup doesn't hide status
    try {
      const sh = await apiService.getSyncShards();
      setShards(sh.data?.items || []);
      setAllowProbe(!!sh.data?.allow_probe);
    } catch (e: any) {
      if (classifyNetErr(e)) {
        try {
          await new Promise(res => setTimeout(res, 500));
          const sh2 = await apiService.getSyncShards();
          setShards(sh2.data?.items || []);
          setAllowProbe(!!sh2.data?.allow_probe);
        } catch (e2: any) {
          setShards([]);
          setShardsLoadError(e2?.response?.data?.detail || e2?.message || 'Failed to load shards');
        }
      } else {
        setShards([]);
        setShardsLoadError(e?.response?.data?.detail || e?.message || 'Failed to load shards');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isAdministrator) return;
    load();
  }, [isAdministrator]);

  const handleIngest = async (kind: string, shard: string) => {
    const key = `${kind}:${shard}`;
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      const resp = await apiService.postIngestShard(kind as any, shard);
      const jobId = resp.data?.job_id;
      if (jobId) {
        setJobStatuses(prev => ({ ...prev, [key]: { running: true, job_id: jobId, kind, shard, message: 'Queued...', percent: 0 } }));
        // Start polling only for this shard
        const poll = async () => {
          try {
            const r = await apiService.getIngestShardStatus(jobId);
            setJobStatuses(prev => ({ ...prev, [key]: r.data || null }));
            if (r.data && (r.data.state === 'finished' || r.data.state === 'error' || r.data.running === false)) {
              return true;
            }
          } catch {
            // ignore
          }
          return false;
        };
        const doneNow = await poll();
        if (doneNow) {
          // refresh shards/status
          load();
          return;
        }
        const interval = setInterval(async () => {
          const done = await poll();
          if (done) {
            clearInterval(interval);
            // refresh shards/status
            load();
          }
        }, 2000);
      }
    } catch (e) {
      // ignore
    }
  };

  const openQueue = async (kind: 'bill'|'member'|'maintenance') => {
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      setQueueDialog(prev => ({ ...prev, open: true, kind, loading: true, error: null }));
      const resp = await apiService.getSyncQueue(kind, { limit: 100, include_dlq: false });
      setQueueDialog(prev => ({ ...prev, items: resp.data?.items || [], total: resp.data?.total || 0, includeDLQ: false, loading: false }));
    } catch (e: any) {
      setQueueDialog(prev => ({ ...prev, loading: false, error: e?.response?.data?.detail || 'Failed to load queue items' }));
    }
  };

  const refreshQueue = async (includeDLQOverride?: boolean) => {
    if (!queueDialog.kind) return;
    try {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      const includeDLQ = includeDLQOverride !== undefined ? includeDLQOverride : queueDialog.includeDLQ;
      setQueueDialog(prev => ({ ...prev, loading: true, error: null, includeDLQ }));
      const resp = await apiService.getSyncQueue(queueDialog.kind, { limit: 100, include_dlq: includeDLQ });
      setQueueDialog(prev => ({ ...prev, items: resp.data?.items || [], total: resp.data?.total || 0, loading: false }));
    } catch (e: any) {
      setQueueDialog(prev => ({ ...prev, loading: false, error: e?.response?.data?.detail || 'Failed to refresh queue' }));
    }
  };

  if (!isAdministrator) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You do not have permission to view this page. Administrator access is required.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>Go to Dashboard</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Sync Inspector
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/admin')}>Admin Home</Button>
        <Button variant="outlined" onClick={() => load()}>Refresh</Button>
      </Stack>

      {(!connectivity.healthOk || !connectivity.infoOk) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Connectivity check failed for {(!connectivity.healthOk && !connectivity.infoOk) ? 'health and info' : (!connectivity.healthOk ? 'health' : 'info')} endpoint.
          This often indicates a TLS/certificate or proxy issue between your browser and api.askthebill.ai. {connectivity.detail ? `(${connectivity.detail})` : ''}
          Try reloading, or test: curl -sS -D - https://api.askthebill.ai/api/health
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Queue Status
          </Typography>
          {!syncStatus ? (
            <Typography variant="body2" color="textSecondary">Loading...</Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Tooltip title="Click to view pending items">
                <Chip clickable onClick={() => openQueue('bill')} label={`Bills: ${syncStatus.queues?.bill ?? '—'}`} variant="outlined" />
              </Tooltip>
              <Tooltip title="Click to view pending items">
                <Chip clickable onClick={() => openQueue('member')} label={`Members: ${syncStatus.queues?.member ?? '—'}`} variant="outlined" />
              </Tooltip>
              <Tooltip title="Click to view pending items">
                <Chip clickable onClick={() => openQueue('maintenance')} label={`Maintenance: ${syncStatus.queues?.maintenance ?? '—'}`} variant="outlined" />
              </Tooltip>
              <Chip label={`Status: ${syncStatus.status || 'unknown'}`} color={syncStatus.status === 'healthy' ? 'success' : (syncStatus.status === 'degraded' ? 'warning' : 'default')} variant="outlined" />
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Shards & Deltas
          </Typography>
          {shardsLoadError && (
            <Alert severity="error" sx={{ mb: 1 }}>{shardsLoadError}</Alert>
          )}
          {allowProbe === false && !shardsLoadError && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Delta estimates are disabled (ALLOW_DIRECT_CONGRESS_API=false). You can still inspect watermarks and ingest shards.
            </Typography>
          )}
          {shards.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No shards found.</Typography>
          ) : (
            <Grid container spacing={1}>
              {shards.map((s, idx) => (
                <Grid item xs={12} md={6} lg={4} key={`${s.kind}-${s.shard}-${idx}`}>
                  <Card variant="outlined">
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1">{s.kind} — {s.shard}</Typography>
                        <Button size="small" variant="contained" onClick={() => handleIngest(s.kind, s.shard)}>Ingest</Button>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                        <Chip label={`WM: ${s.watermark || '—'}`} size="small" />
                        {(() => {
                          const showDelta = s.kind !== 'committee-meeting';
                          const deltaVal = (s.delta_estimate === null || s.delta_estimate === undefined) ? '—' : s.delta_estimate;
                          const label = `Delta: ${showDelta ? deltaVal : '—'}`;
                          const color: any = (showDelta && typeof s.delta_estimate === 'number' && s.delta_estimate > 0) ? 'warning' : 'default';
                          return <Chip label={label} size="small" color={color} />;
                        })()}
                      </Stack>
                      {(() => {
                        const key = `${s.kind}:${s.shard}`;
                        const js = jobStatuses[key];
                        if (!js) return null;
                        return (
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                              <Chip label={js.state || (js.running ? 'running' : 'idle')} size="small" />
                              {js.message && <Typography variant="caption" color="textSecondary">{js.message}</Typography>}
                            </Stack>
                            <LinearProgress variant={typeof js.percent === 'number' ? 'determinate' : 'indeterminate'} value={js.percent || 0} />
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
      {/* Queue Dialog */}
      <Dialog open={queueDialog.open} onClose={() => setQueueDialog(prev => ({ ...prev, open: false }))} fullWidth maxWidth="md">
        <DialogTitle>
          {queueDialog.kind ? `${queueDialog.kind.toUpperCase()} Queue (${queueDialog.total} total)` : 'Queue'}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <FormControlLabel control={<Switch checked={queueDialog.includeDLQ} onChange={async (e) => { setQueueDialog(prev => ({ ...prev, includeDLQ: e.target.checked })); await refreshQueue(); }} />} label="Show DLQ" />
            <Button size="small" onClick={() => refreshQueue()} disabled={queueDialog.loading}>Refresh</Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {queueDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{queueDialog.error}</Alert>
          )}
          {queueDialog.loading ? (
            <Typography variant="body2" color="textSecondary">Loading...</Typography>
          ) : (
            <List dense>
              {queueDialog.items.length === 0 && (
                <Typography variant="body2" color="textSecondary">No items to show.</Typography>
              )}
              {queueDialog.items.map((it: any, idx: number) => (
                <ListItem key={idx} alignItems="flex-start">
                  <ListItemText
                    primary={((() => {
                      try {
                        const obj = (it as any).payload || {};
                        if (obj && obj.congress && obj.bill_type && obj.bill_number && obj.source) {
                          return `AI summary — ${obj.congress}/${String(obj.bill_type).toUpperCase()}/${obj.bill_number} (source: ${obj.source})`;
                        }
                      } catch {}
                      return it.preview ? String(it.preview) : `Item #${it.index}`;
                    })())}
                    secondary={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{typeof it.raw === 'string' ? it.raw : JSON.stringify(it.payload ?? it.raw, null, 2)}</pre>}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SyncInspector;
